import { createAppReducer, apiReducerSwitch } from 'lib/redux/reducerUtils'
import {
  API_CALL_FAILURE,
  API_CALL_REQUEST,
  API_CALL_SUCCESS,
  SET_CUSTOMER_VIEW_TOGGLE,
  SET_AGENT_EXTRA_EARNINGS,
  SET_SOCIETE_REQUESTS_INTERACTIONS,
} from 'actions/actionConstants'
import {
  FETCH_ACCOUNT_AGENT_HUB,
  USER_LOGOUT,
  FETCH_AGENT_STATUS,
  FETCH_OFFER_COMMISSION,
  ONBOARD_AGENCY,
  FETCH_AGENT_AFFILIATION_OPTIONS,
  CLEAR_OFFER_COMMISSION_DATA,
  SET_AGENCY_DETAILS,
  SET_AGENT_PAYOUT_DETAILS,
  FETCH_AGENT_HUB_BOOKING,
  PATCH_AGENT_HUB_CUSTOMER_ID,
} from 'actions/apiActionConstants'

export const AGENT_HUB_INITIAL_STATE: App.AgentHubState = {
  agentHubAccount: {
    account: undefined,
    fetching: false,
    error: undefined,
  },
  agentStatus: {},
  customerView: false,
  onboardingAgency: false,
  onboardingAgencyError: undefined,
  affiliationOptions: {
    fetching: false,
    error: undefined,
    options: {},
  },
  offersCommissionMap: {},
  offersCommissionLoading: {},
  isSavingAgencyDetails: false,
  savingAgencyDetailsError: undefined,
  isSavingAgencyPayoutDetails: false,
  savingAgencyPayoutDetailsError: undefined,
  agentHubBookings: {},
  agentExtraEarnings: 0,
}

const apiCalls = apiReducerSwitch<App.AgentHubState>({
  [FETCH_ACCOUNT_AGENT_HUB]: {
    request: () => {
      return {
        agentHubAccount: {
          account: undefined,
          fetching: true,
          error: undefined,
        },
      }
    },
    success: (state, action) => ({
      agentHubAccount: {
        account: action.data,
        fetching: false,
        error: undefined,
      },
    }),
    failure: (state, action) => ({
      agentHubAccount: {
        account: undefined,
        fetching: false,
        error: action.error.message ?? action.error,
      },
    }),
  },
  [ONBOARD_AGENCY]: {
    request: () => {
      return {
        onboardingAgency: true,
        onboardingAgencyError: undefined,
      }
    },
    success: (state, action) => {
      return {
        onboardingAgency: false,
        agentHubAccount: {
          fetching: false,
          error: undefined,
          account: action.data,
        },
      }
    },
    failure: (state, action) => {
      return {
        onboardingAgency: false,
        onboardingAgencyError: action.error,
      }
    },
  },
  [SET_AGENT_PAYOUT_DETAILS]: {
    request: () => {
      return {
        isSavingAgencyPayoutDetails: true,
        savingAgencyPayoutDetailsError: undefined,
      }
    },
    success: (state, action) => {
      return {
        isSavingAgencyPayoutDetails: false,
        savingAgencyPayoutDetailsError: undefined,
        agentHubAccount: {
          account: action.data,
          fetching: false,
          error: undefined,
        },
      }
    },
    failure: (state, action) => ({
      isSavingAgencyPayoutDetails: false,
      savingAgencyPayoutDetailsError: action.error.message ?? action.error,
    }),
  },
  [SET_AGENCY_DETAILS]: {
    request: () => {
      return {
        isSavingAgencyDetails: true,
        savingAgencyDetailsError: undefined,
      }
    },
    success: (state, action) => {
      return {
        isSavingAgencyDetails: false,
        savingAgencyDetailsError: undefined,
        agentHubAccount: {
          account: action.data,
          fetching: false,
          error: undefined,
        },
      }
    },
    failure: (state, action) => ({
      isSavingAgencyDetails: false,
      savingAgencyDetailsError: action.error.message ?? action.error,
    }),
  },
  [FETCH_AGENT_AFFILIATION_OPTIONS]: {
    request: (state, action) => ({
      affiliationOptions: {
        fetching: true,
        error: undefined,
        options: {
          ...state.affiliationOptions.options,
          [action.key]: [],
        },
      },
    }),
    success: (state, action) => {
      return {
        affiliationOptions: {
          fetching: false,
          error: undefined,
          options: {
            ...state.affiliationOptions.options,
            [action.key]: action.data,
          },
        },
      }
    },
    failure: (state, action) => ({
      affiliationOptions: {
        fetching: false,
        error: action.error,
        options: {
          ...state.affiliationOptions.options,
          [action.key]: [],
        },
      },
    }),
  },
  [FETCH_OFFER_COMMISSION]: {
    request: (state, action) => ({
      offersCommissionLoading: {
        ...state.offersCommissionLoading,
        [action.key]: true,
      },
      offersCommissionMap: {
        ...state.offersCommissionMap,
        [action.key]: undefined,
      },
    }),
    success: (state, action) => ({
      offersCommissionLoading: {
        ...state.offersCommissionLoading,
        [action.key]: false,
      },
      offersCommissionMap: {
        ...state.offersCommissionMap,
        [action.key]: action.data,
      },
    }),
    failure: (state, action) => ({
      offersCommissionLoading: {
        ...state.offersCommissionLoading,
        [action.key]: false,
      },
      offersCommissionMap: {
        ...state.offersCommissionMap,
        [action.key]: undefined,
      },
    }),
  },
  [FETCH_AGENT_HUB_BOOKING]: {
    request: (state, action) => ({
      agentHubBookings: {
        ...state.agentHubBookings,
        [action.key]: {
          fetching: true,
          error: undefined,
        },
      },
    }),
    success: (state, action) => ({
      agentHubBookings: {
        ...state.agentHubBookings,
        [action.key]: {
          fetching: false,
          error: undefined,
          booking: action.data,
        },
      },
    }),
    failure: (state, action) => ({
      agentHubBookings: {
        ...state.agentHubBookings,
        [action.key]: {
          fetching: false,
          error: undefined,
        },
      },
    }),
  },
  [FETCH_AGENT_STATUS]: {
    request: (state, action) => ({
      agentStatus: {
        ...state.agentStatus,
        [action.key]: {
          ...state.agentStatus[action.key],
          fetching: true,
        },
      },
    }),
    success: (state, action) => ({
      agentStatus: {
        ...state.agentStatus,
        [action.key]: {
          data: action.data,
          fetching: false,
          error: undefined,
        },
      },
    }),
    failure: (state, action) => ({
      agentStatus: {
        ...state.agentStatus,
        [action.key]: {
          fetching: false,
          error: action.error.message ?? action.error,
        },
      },
    }),
  },
  [USER_LOGOUT]: {
    success: () => AGENT_HUB_INITIAL_STATE,
  },
  [PATCH_AGENT_HUB_CUSTOMER_ID]: {
    success: (state, action) => ({
      agentHubAccount: {
        ...state.agentHubAccount,
        account: {
          ...state.agentHubAccount.account!,
          customerId: action.data.customerId ?? '',
        },
        error: undefined,
      },
    }),
    failure: (state) => ({
      agentHubAccount: {
        ...state.agentHubAccount,
        error: undefined,
      },
    }),
    request: (state) => ({
      agentHubAccount: {
        ...state.agentHubAccount,
        error: undefined,
      },
    }),
  },
})

export default createAppReducer<App.AgentHubState>(AGENT_HUB_INITIAL_STATE, {
  [API_CALL_REQUEST]: (state, action) => apiCalls(state, action),
  [API_CALL_FAILURE]: (state, action) => apiCalls(state, action),
  [API_CALL_SUCCESS]: (state, action) => apiCalls(state, action),
  [SET_CUSTOMER_VIEW_TOGGLE]: (state, action) => ({
    customerView: action.value,
  }),
  [SET_AGENT_EXTRA_EARNINGS]: (_state, action) => ({
    agentExtraEarnings: action.amount,
  }),
  [CLEAR_OFFER_COMMISSION_DATA]: (state, action) => ({
    offersCommissionLoading: {
      ...state.offersCommissionLoading,
      [action.key]: false,
    },
    offersCommissionMap: {
      ...state.offersCommissionMap,
      [action.key]: undefined,
    },
  }),
  [SET_SOCIETE_REQUESTS_INTERACTIONS]: (state, action) => {
    if (!state.agentHubAccount.account) return {}
    return {
      agentHubAccount: {
        ...state.agentHubAccount,
        account: {
          ...state.agentHubAccount.account,
          societeRequestsInteractions: {
            ...state.agentHubAccount.account.societeRequestsInteractions,
            ...action.interactions,
          },
        },
      },
    }
  },
})
